import type { AppState, LocationSlot } from './types.js';
import { lookupTimezone } from './timezone.js';

// === Shareable URL state ===
//
// The most reliable way to settle a scheduling argument is to send the other
// person the exact view you are looking at. Without this, the planner's state
// lives only in localStorage and cannot leave the browser it was typed into.
//
// Encoding uses "," between fields and ";" between records — both are escaped
// by encodeURIComponent, so no city name or label can break the format.

const VERSION = '1';

export interface ShareState {
  slots: LocationSlot[];
  /** Reference date as YYYY-MM-DD, or null for "today" */
  date: string | null;
  anchorIndex: number;
}

function encodeDate(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** Build the URL hash fragment representing the current planner state */
export function encodeState(slots: LocationSlot[], date: Date | null, anchorIndex: number): string {
  const records: string[] = [
    [VERSION, date ? encodeDate(date) : '-', String(anchorIndex)].join(','),
  ];

  for (const s of slots) {
    records.push(
      [
        encodeURIComponent(s.city.name),
        encodeURIComponent(s.timezone.id),
        String(s.workingStart),
        String(s.workingEnd),
        encodeURIComponent(s.label === s.city.name ? '' : s.label),
      ].join(',')
    );
  }

  return `#s=${records.join(';')}`;
}

/** Parse a URL hash fragment back into planner state. Returns null if absent or unusable. */
export function decodeState(hash: string, data: AppState): ShareState | null {
  const m = /[#&]s=([^&]+)/.exec(hash);
  if (!m || !m[1]) return null;

  const records = m[1].split(';');
  const meta = records[0]?.split(',');
  if (!meta || meta[0] !== VERSION) return null;

  const date = meta[1] && meta[1] !== '-' && /^\d{4}-\d{2}-\d{2}$/.test(meta[1]) ? meta[1] : null;
  const anchorIndex = Number.parseInt(meta[2] ?? '0', 10);

  const slots: LocationSlot[] = [];
  for (const rec of records.slice(1)) {
    const f = rec.split(',');
    if (f.length < 4) continue;

    const cityName = decodeURIComponent(f[0]!);
    const tzId = decodeURIComponent(f[1]!);

    const timezone = lookupTimezone(data.timezones, tzId);
    if (!timezone) continue;

    // Prefer the exact city record; fall back to any city in that zone so a
    // link still resolves if the city list changes between deploys.
    const city =
      data.cities.find(c => c.name === cityName && c.timezone === tzId) ??
      data.cities.find(c => c.timezone === tzId);
    if (!city) continue;

    const workingStart = clampHour(Number.parseInt(f[2]!, 10), 9);
    const workingEnd = clampHour(Number.parseInt(f[3]!, 10), 17);
    const rawLabel = f[4] ? decodeURIComponent(f[4]) : '';

    slots.push({
      city,
      timezone,
      label: rawLabel || city.name,
      workingStart,
      workingEnd,
    });
  }

  if (slots.length === 0) return null;

  return {
    slots,
    date,
    anchorIndex: Number.isFinite(anchorIndex) && anchorIndex >= 0 && anchorIndex < slots.length ? anchorIndex : 0,
  };
}

function clampHour(v: number, fallback: number): number {
  return Number.isFinite(v) && v >= 0 && v <= 23 ? v : fallback;
}

/** Absolute shareable URL for the current state */
export function buildShareUrl(slots: LocationSlot[], date: Date | null, anchorIndex: number): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}${encodeState(slots, date, anchorIndex)}`;
}
